/**
 * @file src/world/worldTextureUrls.js
 * @description Which image files a world release actually draws with.
 *
 * This used to live inside `OrbitersPlayMode.js`, which was fine while the renderer was the only
 * thing that ever asked. An offline download asks the same question for a different reason — it
 * needs the files BEFORE anything is drawn — and answering it twice would mean a download that
 * quietly fetches a different texture from the one the scene later requests, which is the failure
 * nobody would notice until there was no network to cover it. So the answer is given once, here,
 * and both callers read it.
 *
 * A world names its textures in several places, because releases from different eras of the editor
 * put them in different fields. The candidate lists below are that history, not indecision: the
 * first field that yields something is the one that release used.
 */
import { resolvePublicUrl } from 'entangled-worlds-orbiters-shared/config';

/**
 * A stored value into a fetchable URL. Absolute URLs and data URIs pass through; anything else is
 * a storage key and is resolved against the public base.
 * @param {*} path
 * @returns {string|null}
 */
export function resolveAssetUrl(path) {
  if (typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) {
    return trimmed;
  }
  return resolvePublicUrl(trimmed);
}

/** The preview block a release keeps its look in, under whichever name its era used. */
export function extractPreview(metadata = {}) {
  return (
    metadata.entangledWorldPreview ||
    metadata.preview ||
    metadata?.step_two?.entangled_world_preview ||
    {}
  );
}

/** The planet surface texture. */
export function resolveTextureUrl(world, preview = {}) {
  const assets = world?.assets || {};
  const metadata = world?.metadata || {};
  const sphere = preview.sphere || metadata.sphere || {};
  const worldTexture = metadata.worldTexture || metadata.texture || {};
  const previewWorldTexture = preview.worldTexture || {};

  const candidates = [
    worldTexture.url,
    worldTexture.textureUrl,
    worldTexture.textureURL,
    metadata.selectedTextureURL,
    metadata.textureURL,
    metadata.textureUrl,
    metadata.texture?.url,
    assets.selectedTextureURL,
    assets.selectedTextureUncompressedURL,
    previewWorldTexture.url,
    previewWorldTexture.textureUrl,
    previewWorldTexture.textureURL,
    assets.selectedTextureKey,
    worldTexture.selected,
    previewWorldTexture.selected,
    sphere.textureUrl,
    sphere.textureURL,
  ];

  for (const candidate of candidates) {
    const resolved = resolveAssetUrl(candidate);
    if (resolved) return resolved;
  }

  return null;
}

/** The cloud layer's texture — null when this world draws no clouds. */
export function resolveCloudsTextureUrl(world, preview = {}) {
  const metadata = world?.metadata || {};
  const clouds = preview.clouds || metadata.clouds || {};
  return resolveAssetUrl(clouds.textureKey || clouds.textureUrl);
}

/** The moons' shared texture — null when this world has none, or draws them untextured. */
export function resolveMoonsTextureUrl(world, preview = {}) {
  const metadata = world?.metadata || {};
  const previewMoons = preview.moons || metadata.moons || {};
  return resolveAssetUrl(
    previewMoons.textureKey ||
      previewMoons.textureUrl ||
      previewMoons.textureURL ||
      metadata?.moons?.textureKey ||
      metadata?.moons?.textureUrl ||
      metadata?.moons?.textureURL ||
      null,
  );
}

/**
 * Every texture URL one world release draws with, de-duplicated.
 *
 * Two or three files per world, not the catalog's forty-three: the catalog is what a world COULD
 * have picked, and caching all of it would pay for thirty-nine files nobody is going to look at.
 * @param {object} world a normalized world release
 * @returns {string[]}
 */
export function resolveWorldTextureUrls(world) {
  if (!world) return [];
  const preview = extractPreview(world.metadata || {});
  const urls = [
    resolveTextureUrl(world, preview),
    resolveCloudsTextureUrl(world, preview),
    resolveMoonsTextureUrl(world, preview),
  ].filter(Boolean);
  return [...new Set(urls)];
}
